import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { getSettings } from "./settings-api";
import { getAccessToken } from "./api";

// Helper to normalize logo URL to use current API base
function normalizeLogoUrl(logoUrl: string | null): string | null {
  if (!logoUrl) return null;

  // Extract the path and query params from the logo URL (/api/branding/{tenantId}/logo?v=...)
  const match = logoUrl.match(/\/api\/branding\/[^/]+\/logo(\?.*)?$/);
  if (match) {
    // Reconstruct using current API base, preserving query params
    const apiBase = import.meta.env.VITE_API_URL || "";
    const queryParams = match[1] || "";
    return `${apiBase}/api/branding/${logoUrl.match(/\/api\/branding\/([^/]+)\/logo/)?.[1]}/logo${queryParams}`;
  }

  return logoUrl;
}

// ════════════════════════════════════════════════════════════════════════════
// Branding Context
// ════════════════════════════════════════════════════════════════════════════
// Provides app-wide access to tenant branding (logo, colors) for the dashboard.
// Used by DashboardLayout for blob colors and Sidebar for logo display.

interface BrandingData {
  logoUrl: string | null;
  extractedColors: string[];
  brandPrimaryColor: string;
  brandSecondaryColor: string;
  brandBackgroundColor: string;
  brandTextColor: string;
  brandAccentColor: string;
}

interface BrandingContextType {
  branding: BrandingData | null;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
  /** Logo URL with cache buster to force refresh after uploads */
  getCacheBustedLogoUrl: () => string | null;
}

const defaultBranding: BrandingData = {
  logoUrl: null,
  extractedColors: [],
  brandPrimaryColor: "#1a2533",
  brandSecondaryColor: "#6b7a8f",
  brandBackgroundColor: "#f7f9fb",
  brandTextColor: "#1a2533",
  brandAccentColor: "#6b7a8f",
};

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  isLoading: true,
  refreshBranding: async () => {},
  getCacheBustedLogoUrl: () => null,
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Version counter to bust browser cache when logo is uploaded
  const [logoVersion, setLogoVersion] = useState(() => Date.now());

  const fetchBranding = async () => {
    const token = getAccessToken();
    if (!token) {
      setBranding(defaultBranding);
      setIsLoading(false);
      return;
    }

    try {
      const settings = await getSettings();
      setBranding({
        logoUrl: normalizeLogoUrl(settings.logoUrl),
        extractedColors: settings.logoExtractedColors || [],
        brandPrimaryColor: settings.brandPrimaryColor,
        brandSecondaryColor: settings.brandSecondaryColor,
        brandBackgroundColor: settings.brandBackgroundColor,
        brandTextColor: settings.brandTextColor,
        brandAccentColor: settings.brandAccentColor,
      });
    } catch (error) {
      console.error("Failed to fetch branding:", error);
      setBranding(defaultBranding);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  const refreshBranding = async () => {
    // Update version to bust browser cache for logo
    setLogoVersion(Date.now());
    await fetchBranding();
  };

  // Get logo URL with cache buster appended
  const getCacheBustedLogoUrl = (): string | null => {
    if (!branding?.logoUrl) return null;
    const separator = branding.logoUrl.includes("?") ? "&" : "?";
    return `${branding.logoUrl}${separator}v=${logoVersion}`;
  };

  return (
    <BrandingContext.Provider value={{ branding, isLoading, refreshBranding, getCacheBustedLogoUrl }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextType {
  return useContext(BrandingContext);
}

// Helper to generate blob colors from extracted colors
// Takes up to 4 extracted colors and creates 5 gradient-based blob colors
export function generateBlobColors(extractedColors: string[]): string[] | undefined {
  if (!extractedColors || extractedColors.length === 0) {
    return undefined; // Use default blob colors
  }

  // Ensure we have at least 4 colors (pad with variations if needed)
  const colors: string[] = [...extractedColors];
  while (colors.length < 4) {
    // Create variations of existing colors
    const baseColor = colors[colors.length - 1] ?? colors[0] ?? "#6b7a8f";
    colors.push(adjustColorBrightness(baseColor, colors.length * 10));
  }

  // Generate 5 gradient strings for the blobs
  const c0 = colors[0] ?? "#6b7a8f";
  const c1 = colors[1] ?? "#6b7a8f";
  const c2 = colors[2] ?? "#6b7a8f";
  const c3 = colors[3] ?? "#6b7a8f";

  return [
    `linear-gradient(135deg, ${c0}, ${adjustColorBrightness(c0, -20)})`,
    `linear-gradient(135deg, ${c1}, ${adjustColorBrightness(c1, 20)})`,
    `linear-gradient(135deg, ${c2}, ${adjustColorBrightness(c2, -15)})`,
    `linear-gradient(135deg, ${c3}, ${adjustColorBrightness(c3, 15)})`,
    `linear-gradient(135deg, ${c0}, ${c2})`,
  ];
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex: string, percent: number): string {
  // Remove # if present
  const color = hex.replace("#", "");

  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Adjust brightness
  const adjust = (value: number) => {
    const adjusted = Math.round(value + (255 * percent) / 100);
    return Math.max(0, Math.min(255, adjusted));
  };

  const newR = adjust(r);
  const newG = adjust(g);
  const newB = adjust(b);

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}
